<template>
  <Dropdown
    v-if="hasContent"
    ref="dropdownRef"
    v-bind="$attrs"
    v-model:open="innerOpen"
    :trigger="trigger"
    :position="position"
    :gap="gap"
    :use-min-width="false"
  >
    <template #trigger="{ active }">
      <slot name="trigger" :selected="hasActiveItem" :active="active" />
    </template>

    <template #content>
      <div :class="['top-dropdown-content', mode]" v-show="isReady">
        <!-- 简单列表模式 -->
        <template v-if="mode === 'simple' && items.length">
          <NuxtLink
            v-for="(item, index) in items"
            :key="index"
            :to="item.route || item.link || '#'"
            :target="item.target"
            :class="['menu-item', { hidden: item.hidden, active: isActiveRoute(item) }]"
            @click="handleItemClick(item)"
          >
            <component v-if="item.icon" :is="item.icon" class="menu-icon" />
            <span class="menu-label">{{ item.name }}</span>
          </NuxtLink>
        </template>

        <!-- 分组模式 -->
        <template v-if="mode === 'grouped'">
          <div v-for="(group, groupIndex) in groups" :key="groupIndex" class="menu-group">
            <div v-if="group.title" class="group-title">{{ group.title }}</div>
            <div class="group-divider" />
            <div class="group-items">
              <NuxtLink
                v-for="(item, itemIndex) in group.items"
                :key="itemIndex"
                :to="item.route || item.link || '#'"
                :target="item.target"
                :class="['menu-item', { hidden: item.hidden, active: isActiveRoute(item) }]"
                @click="handleItemClick(item)"
              >
                <component v-if="item.icon" :is="item.icon" class="menu-icon" />
                <div class="menu-content">
                  <span class="menu-label">{{ item.name }}</span>
                </div>
              </NuxtLink>
            </div>
          </div>
        </template>

        <!-- 自定义内容插槽 -->
        <slot name="content" />
      </div>
    </template>
  </Dropdown>
  <!-- 没有内容时只渲染 trigger，不带下拉功能 -->
  <slot v-else name="trigger" :selected="hasActiveItem" :active="hasActiveItem" />
</template>

<script setup lang="ts">
import Dropdown from '@rendora/design/components/dropdown/Dropdown.vue';
import type { ComponentPublicInstance } from 'vue';
import { type Nav } from '~/hooks/useNav';

const route = useRoute();

export interface MenuGroup {
  title?: string;
  items: Nav[];
}

interface TopDropdownProps {
  trigger?: 'click' | 'hover';
  position?: 'bottomCenter' | 'bottomLeft' | 'bottomRight' | 'right';
  gap?: number | [number, number];
  open?: boolean;
  mode?: 'simple' | 'grouped';
  nav?: Nav;
  items?: Nav[];
  groups?: MenuGroup[];
}

const props = withDefaults(defineProps<TopDropdownProps>(), {
  trigger: 'hover',
  position: 'bottomLeft',
  gap: 8,
  mode: 'simple',
  items: () => [],
  groups: () => [],
});

const emit = defineEmits<{
  'update:open': [value: boolean];
  'item-click': [item: Nav];
}>();

// 判断是否有内容
const hasContent = computed(() => {
  if (props.mode === 'simple') {
    return props.items.length > 0;
  }
  if (props.mode === 'grouped') {
    return props.groups.length > 0 && props.groups.some(group => group.items.length > 0);
  }
  return false;
});

// 判断菜单中是否有激活的路由项
const hasActiveItem = computed(() => {
  if (props.nav && isActiveRoute(props.nav)) {
    return true;
  }
  if (props.mode === 'simple') {
    return props.items.some(item => isActiveRoute(item));
  }
  if (props.mode === 'grouped') {
    return props.groups.some(group => group.items.some(item => isActiveRoute(item)));
  }
  return false;
});

const isActiveRoute = (item: Nav) => {
  if (!item.route) return false;

  // 获取当前路由路径
  const currentPath = route.path;

  // 如果是字符串路由，直接比较
  if (typeof item.route === 'string') {
    return currentPath === item.route;
  }

  return false;
};

const innerOpen = computed({
  get: () => props.open,
  set: value => emit('update:open', value),
});

const dropdownRef = ref<ComponentPublicInstance<typeof Dropdown> | null>(null);

const handleItemClick = (item: Nav) => {
  emit('item-click', item);

  // 点击后关闭菜单
  dropdownRef.value?.close();
};
const hasRendered = ref(false);
const isReady = ref(false);

onMounted(() => {
  // 组件挂载后预渲染内容
  nextTick(() => {
    isReady.value = true;
  });
});

watch(innerOpen, value => {
  if (value) {
    hasRendered.value = true;
  }
});
</script>

<style lang="scss" scoped>
.top-dropdown-content {
  background: var(--x-white-1);
  border-radius: 8px;
  padding: 8px;
  border: 1px solid var(--x-back-4);
  box-shadow: var(--x-shadow);
  will-change: transform, opacity; // 优化动画性能
  &.simple {
    // min-width: 120px;
    width: max-content;
  }

  &.grouped {
    width: max-content;
    min-width: 320px;
    padding-top: 16px;
  }
}

/* 简单模式样式 */
.top-dropdown-content.simple {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* 分组模式样式 */
.top-dropdown-content.grouped {
  display: flex;
  gap: 32px;
}

.menu-group {
  display: flex;
  flex-direction: column;
  min-width: 160px;
}

.group-title {
  font-weight: 400;
  color: var(--x-text-5);
  padding: 8px 12px;
  font-size: var(--x-text-16-size);
  line-height: var(--x-text-16-line-height);
}

.group-divider {
  margin: 16px 0;
  height: 1px;
  width: 100%;
  background: var(--x-back-4);
}

.group-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* 菜单项通用样式 */
.menu-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: var(--x-text-16-size);
  line-height: var(--x-text-16-line-height);
  color: var(--x-text-1);

  &.router-link-exact-active,
  &:hover {
    background: var(--x-back-4);
  }
}

.menu-item.active {
  background: var(--x-back-4);
}

.menu-item.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.menu-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: currentColor;
}

.menu-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.menu-label {
  font-size: 16px;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .top-dropdown-content {
    min-width: 160px;
  }
}
html[lang='en-US'] {
  .menu-group {
    min-width: 240px;
  }
}
</style>
